/**
 * Principal Business - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  TrendingUp, Users, Baby, Calendar,
  RefreshCw, IndianRupee, Brain, BarChart3,
  Home, FileText, Eye, ClipboardList, UserCheck, Phone, Shield
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalBusiness = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [businessData, setBusinessData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [principalName, setPrincipalName] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    fetchData();
  }, [navigate, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('principal_token');
      const [dashRes, businessRes, paymentsRes, earningsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/principal/business-summary?token=${token}`),
        axios.get(`${BACKEND_URL}/api/principal/observer-payments?token=${token}&month=${selectedMonth}`),
        axios.get(`${BACKEND_URL}/api/principal/my-earnings?token=${token}`)
      ]);
      
      setPrincipalName(dashRes.data?.principal?.name || 'Principal');
      setBusinessData(businessRes.data);
      setPaymentsData(paymentsRes.data);
      setEarningsData(earningsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: "Error", description: "Failed to load business data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading business data...</p>
        </div>
      </div>
    );
  }

  const metrics = businessData?.business_metrics || {};
  const paymentSummary = paymentsData?.summary || {};
  const earnings = earningsData?.earnings || {};

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName}
      onLogout={handleLogout}
    >
      <div data-testid="principal-business-page" className="font-body">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Business & Payments
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Track revenue, payments, and earnings</p>
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="month" 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-sanjaya-navy"
            />
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="h-10 border-sanjaya-navy/30 text-sanjaya-navy"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div className="bg-sanjaya-teal text-white rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <IndianRupee className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <p className="text-lg sm:text-2xl font-bold font-heading">₹{metrics.total_monthly_revenue?.toLocaleString() || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Monthly Revenue</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-navy text-white rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Baby className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <p className="text-lg sm:text-2xl font-bold font-heading">{metrics.total_children || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Total Children</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-navy-light text-white rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <p className="text-lg sm:text-2xl font-bold font-heading">{metrics.sessions_this_month || 0}</p>
                <p className="text-xs sm:text-sm text-white/80">Sessions This Month</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-orange text-sanjaya-navy rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <IndianRupee className="w-6 h-6 sm:w-8 sm:h-8" />
              <div>
                <p className="text-lg sm:text-2xl font-bold font-heading">₹{earnings.total_earnings?.toLocaleString() || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-navy/80">Your Earnings</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
          <Button 
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={`text-xs sm:text-sm ${activeTab === 'overview' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <TrendingUp className="w-4 h-4 mr-2" /> Overview
          </Button>
          <Button 
            variant={activeTab === 'observer-payments' ? 'default' : 'outline'}
            onClick={() => setActiveTab('observer-payments')}
            className={`text-xs sm:text-sm ${activeTab === 'observer-payments' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <Users className="w-4 h-4 mr-2" /> Observer Payments
          </Button>
          <Button 
            variant={activeTab === 'my-earnings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('my-earnings')}
            className={`text-xs sm:text-sm ${activeTab === 'my-earnings' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
          >
            <IndianRupee className="w-4 h-4 mr-2" /> My Earnings
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Business Metrics */}
            <Card className="border-2 border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg font-heading text-sanjaya-navy">Business Metrics</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Revenue and enrollment overview</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 bg-sanjaya-navy/10 rounded-xl">
                    <p className="text-xs sm:text-sm text-sanjaya-navy">Premium Children</p>
                    <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{metrics.premium_children || 0}</p>
                  </div>
                  <div className="p-3 sm:p-4 bg-slate-100 rounded-xl">
                    <p className="text-xs sm:text-sm text-slate-600">Standard Children</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-700 font-heading">{metrics.standard_children || 0}</p>
                  </div>
                </div>
                
                <div className="p-3 sm:p-4 bg-sanjaya-teal/10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs sm:text-sm text-sanjaya-teal">Monthly Revenue</p>
                      <p className="text-2xl sm:text-3xl font-bold text-sanjaya-teal font-heading">₹{metrics.total_monthly_revenue?.toLocaleString() || 0}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-sanjaya-teal/30" />
                  </div>
                </div>
                
                <div className="p-3 sm:p-4 bg-sanjaya-navy/10 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs sm:text-sm text-sanjaya-navy">Sessions This Month</p>
                      <p className="text-2xl sm:text-3xl font-bold text-sanjaya-navy font-heading">{metrics.sessions_this_month || 0}</p>
                    </div>
                    <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-sanjaya-navy/30" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Overview */}
            <Card className="border-2 border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg font-heading text-sanjaya-navy">Your Team</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Observers under your supervision</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 sm:p-4 bg-sanjaya-navy/10 rounded-xl">
                  <p className="text-xs sm:text-sm text-sanjaya-navy">Total Observers</p>
                  <p className="text-2xl sm:text-3xl font-bold text-sanjaya-navy font-heading">{businessData?.team?.total_observers || 0}</p>
                </div>
                
                <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                  {businessData?.team?.observers?.map(observer => (
                    <div key={observer.id} className="p-3 bg-slate-50 rounded-xl flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-sm sm:text-base text-slate-800">{observer.name}</p>
                        <p className="text-xs sm:text-sm text-slate-500">{observer.email}</p>
                      </div>
                      <span className="text-xs sm:text-sm text-sanjaya-teal font-semibold">₹{observer.session_rate || 100}/session</span>
                    </div>
                  )) || (
                    <p className="text-slate-500 text-center py-4 text-sm">No observers found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'observer-payments' && (
          <Card className="border-2 border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg font-heading text-sanjaya-navy">Observer Payments - {selectedMonth}</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Track payment status for all observers</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="p-3 sm:p-4 bg-sanjaya-navy/10 rounded-xl text-center">
                  <p className="text-xs sm:text-sm text-sanjaya-navy">Total Sessions</p>
                  <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{paymentSummary.total_sessions || 0}</p>
                </div>
                <div className="p-3 sm:p-4 bg-sanjaya-teal/10 rounded-xl text-center">
                  <p className="text-xs sm:text-sm text-sanjaya-teal">Total Earned</p>
                  <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">₹{paymentSummary.total_earnings?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 sm:p-4 bg-sanjaya-teal/10 rounded-xl text-center">
                  <p className="text-xs sm:text-sm text-sanjaya-teal">Total Paid</p>
                  <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">₹{paymentSummary.total_paid?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 sm:p-4 bg-sanjaya-orange/20 rounded-xl text-center">
                  <p className="text-xs sm:text-sm text-sanjaya-orange">Pending</p>
                  <p className="text-xl sm:text-2xl font-bold text-sanjaya-orange font-heading">₹{paymentSummary.total_pending?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Observer Payment Details */}
              <div className="space-y-3">
                {paymentsData?.observer_payments?.length === 0 ? (
                  <p className="text-center text-slate-500 py-8 text-sm sm:text-base">No payment data for this month</p>
                ) : (
                  paymentsData?.observer_payments?.map(item => (
                    <div key={item.observer.id} className="p-4 border-2 border-slate-200 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-bold text-sm sm:text-base text-slate-800 font-heading">{item.observer.name}</p>
                          <p className="text-xs sm:text-sm text-slate-500">{item.observer.email}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          item.payment_status === 'paid' 
                            ? 'bg-sanjaya-teal/10 text-sanjaya-teal' 
                            : 'bg-sanjaya-orange/20 text-sanjaya-orange'
                        }`}>
                          {item.payment_status === 'paid' ? 'Fully Paid' : 'Pending'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <p className="text-slate-500">Sessions</p>
                          <p className="font-semibold text-slate-700">{item.sessions.total} ({item.sessions.completed} completed)</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Rate</p>
                          <p className="font-semibold text-slate-700">₹{item.observer.session_rate}/session</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Total Earned</p>
                          <p className="font-semibold text-sanjaya-teal">₹{item.earnings.total_earned.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Pending</p>
                          <p className={`font-semibold ${item.earnings.pending > 0 ? 'text-sanjaya-orange' : 'text-sanjaya-teal'}`}>
                            ₹{item.earnings.pending.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'my-earnings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Earnings Summary */}
            <Card className="border-2 border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg font-heading text-sanjaya-navy">Your Earnings</CardTitle>
                <CardDescription className="text-xs sm:text-sm">From parent consultations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 sm:p-4 bg-sanjaya-orange/20 rounded-xl">
                  <p className="text-xs sm:text-sm text-sanjaya-orange">Consultation Rate</p>
                  <p className="text-2xl sm:text-3xl font-bold text-sanjaya-orange font-heading">
                    ₹{earningsData?.principal?.consultation_rate?.toLocaleString() || 500}/session
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 bg-sanjaya-navy/10 rounded-xl">
                    <p className="text-xs sm:text-sm text-sanjaya-navy">Consultations</p>
                    <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{earnings.total_consultations || 0}</p>
                  </div>
                  <div className="p-3 sm:p-4 bg-sanjaya-teal/10 rounded-xl">
                    <p className="text-xs sm:text-sm text-sanjaya-teal">Total Earnings</p>
                    <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">₹{earnings.total_earnings?.toLocaleString() || 0}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 bg-sanjaya-teal/10 rounded-xl">
                    <p className="text-xs sm:text-sm text-sanjaya-teal">Paid</p>
                    <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">₹{earnings.total_paid?.toLocaleString() || 0}</p>
                  </div>
                  <div className="p-3 sm:p-4 bg-sanjaya-orange/20 rounded-xl">
                    <p className="text-xs sm:text-sm text-sanjaya-orange">Pending</p>
                    <p className="text-xl sm:text-2xl font-bold text-sanjaya-orange font-heading">₹{earnings.pending?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Breakdown */}
            <Card className="border-2 border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg font-heading text-sanjaya-navy">Monthly Breakdown</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Earnings by month</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 sm:max-h-80 overflow-y-auto">
                  {Object.entries(earningsData?.monthly_breakdown || {}).length === 0 ? (
                    <p className="text-center text-slate-500 py-8 text-sm">No earnings data yet</p>
                  ) : (
                    Object.entries(earningsData?.monthly_breakdown || {})
                      .sort((a, b) => b[0].localeCompare(a[0]))
                      .map(([month, data]) => (
                        <div key={month} className="p-3 bg-slate-50 rounded-xl flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-sm sm:text-base text-slate-800">{month}</p>
                            <p className="text-xs sm:text-sm text-slate-500">{data.consultations} consultations</p>
                          </div>
                          <p className="text-base sm:text-lg font-bold text-sanjaya-teal">₹{data.earnings.toLocaleString()}</p>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
};

export default PrincipalBusiness;
